import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Home, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const user = await login(username, password);
      toast.success(`Welcome back, ${user.full_name || user.username}!`);
      if (user.must_change_password) {
        navigate("/change-password");
      } else if (!user.has_selected_role && user.role !== "admin") {
        // First login after registration approval - go to role selection
        navigate("/select-role");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Invalid credentials";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="login-page">
      {/* Form Section */}
      <div className="auth-form-section bg-background">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo/Brand */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary">WilyRash Homes</h1>
              <p className="text-sm text-text-muted">Tenant Management System</p>
            </div>
          </div>

          <Card className="border-stone-200 shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-semibold">Welcome back</CardTitle>
              <CardDescription>Enter your credentials to access your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    data-testid="login-username-input"
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200 focus:border-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200 focus:border-primary pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <Button 
                  type="submit" 
                  data-testid="login-submit-btn"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary-800 text-white font-medium transition-all active:scale-95"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-text-muted">Don't have an account? </span>
                <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
                  Register here
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Section */}
      <div 
        className="auth-image-section"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1666532937398-f7cdbb7d9588?crop=entropy&cs=srgb&fm=jpg&q=85')",
        }}
      >
        <div className="auth-image-overlay" />
        <div className="relative z-10 p-12 flex flex-col justify-end h-full text-white">
          <h2 className="text-3xl font-bold mb-4">Find Your Perfect Home</h2>
          <p className="text-lg text-white/80">
            Join WilyRash Homes community and experience comfortable living at its finest.
          </p>
        </div>
      </div>
    </div>
  );
}
